import { GridCellParams, GridValidRowModel } from '@mui/x-data-grid-pro'
import SingleDropdown, {
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import { input_borderHover, text_colorTextSecondary } from 'ext-ui/style/colors'
import React from 'react'
import FinalApprovalApprovedIcon from 'src/assets/svgs/FinalApprovalApprovedIcon.svg'
import FinalApprovalFlaggedIcon from 'src/assets/svgs/FinalApprovalFlaggedIcon.svg'
import FinalApprovalPendingIcon from 'src/assets/svgs/FinalApprovalPendingIcon.svg'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import SvgCheckNew from 'src/components/Svgs/SvgCheckNew'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import { useTranslate } from 'src/localization/Trns'
import { TransactionFinalApprovalStatus } from 'src/models/transaction.models'
import { usePatchTransactionFinalApprovalStatus } from 'src/queries/expenseReview.queries'
import styled from 'styled-components'

type FinalApprovalDropdownCellProps<Row extends GridValidRowModel> = {
  filters: TransactionsFiltersBody
  params: GridCellParams<Row>
  status?: TransactionFinalApprovalStatus
}

const presentationForStatus = {
  [TransactionFinalApprovalStatus.PendingReview]: {
    label: 'Pending',
    menuLabel: 'Pending Approval',
    pillColor: 'pending' as const,
    testId: 'pending',
    menuIcon: FinalApprovalPendingIcon,
    topLevelIcon: FinalApprovalPendingIcon,
  },
  [TransactionFinalApprovalStatus.Flagged]: {
    label: 'Flagged',
    menuLabel: 'Flagged',
    pillColor: 'severe' as const,
    testId: 'flagged',
    menuIcon: FinalApprovalFlaggedIcon,
    topLevelIcon: FinalApprovalFlaggedIcon,
  },
  [TransactionFinalApprovalStatus.Reviewed]: {
    label: 'Approved',
    menuLabel: 'Approve',
    pillColor: 'highlight' as const,
    testId: 'approved',
    menuIcon: FinalApprovalApprovedIcon,
    topLevelIcon: undefined,
  },
}

const FinalApprovalOptionPill = styled(Pill)`
  & + .option-label-wrapper {
    display: none;
  }
`

const FinalApprovalOptionContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

function FinalApprovalDropdownCell<Row extends GridValidRowModel>({
  filters,
  params,
  status = TransactionFinalApprovalStatus.PendingReview,
}: FinalApprovalDropdownCellProps<Row>): React.ReactElement {
  const translate = useTranslate()
  const { mutateAsync: patchFinalApprovalStatus } =
    usePatchTransactionFinalApprovalStatus()
  const transactionId = params.row.transaction.id
  const selected = presentationForStatus[status]
  const options = Object.entries(presentationForStatus).map(
    ([value, presentation]) => ({
      value,
      label: translate(presentation.label),
      'aria-label': translate(presentation.menuLabel),
      icon: (
        <FinalApprovalOptionPill
          color={presentation.pillColor as PillProps['color']}
          data-testid={`final-approval-option-${presentation.testId}`}
          size="small"
        >
          <FinalApprovalOptionContent>
            <img
              alt=""
              data-testid={`final-approval-option-${presentation.testId}-icon`}
              height={16}
              src={presentation.menuIcon}
              width={16}
            />
            {translate(presentation.menuLabel)}
          </FinalApprovalOptionContent>
        </FinalApprovalOptionPill>
      ),
    }),
  )

  const handleSelect = (selectedValue: DropdownValue): Promise<void> =>
    patchFinalApprovalStatus({
      body: {
        transactionIds: [transactionId],
        finalApprovalStatus:
          selectedValue.value as TransactionFinalApprovalStatus,
      },
      filters,
    }).then(() => undefined)

  const stopPropagation = (event: React.SyntheticEvent): void => {
    event.stopPropagation()
  }

  return (
    <DataGridCellWrapper
      compact
      params={params}
      onClick={stopPropagation}
      onFocus={stopPropagation}
      onKeyDown={stopPropagation}
    >
      <SingleDropdown
        clearable={false}
        containerStyle={{
          alignItems: 'center',
          borderColor: input_borderHover,
          borderRadius: 4,
          boxSizing: 'border-box',
          display: 'flex',
          flexShrink: 0,
          gap: 4,
          height: 32,
          justifyContent: 'space-between',
          overflow: 'hidden',
          padding: '4px 8px',
          width: 116,
        }}
        data-testid={`final-approval-${transactionId}`}
        dropdownContainer={document.body}
        dropdownMenuWidth={200}
        dropdownOptionsCount={3}
        dropdownPlacement="bottom-end"
        hideSelectedIcon
        compactOptions
        iconLeft={
          status === TransactionFinalApprovalStatus.Reviewed ? (
            <SvgCheckNew
              data-testid={`final-approval-${transactionId}-status-icon`}
              height={16}
              stroke={text_colorTextSecondary}
              width={16}
            />
          ) : (
            <img
              alt=""
              data-testid={`final-approval-${transactionId}-status-icon`}
              height={16}
              src={selected.topLevelIcon}
              width={16}
            />
          )
        }
        noneLabel=""
        onSelect={handleSelect}
        options={options}
        searchable={false}
        selectedValue={{
          value: status,
          label: translate(selected.label),
        }}
        style={{ minWidth: 0, padding: 0 }}
        variant="small"
      />
    </DataGridCellWrapper>
  )
}

export default FinalApprovalDropdownCell
